import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import './index.scss';
import {useTranslation} from '../../hooks/useTranslation';
const months = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export default function MonthPicker({
  mode = 'range', // 'single' | 'range' | 'multiple'
  onChange,
  maxSelectable = 12,
  label,
  disabledFrom,
  disabledTo,
  disabled = false,
  initialYear,
}) {
  const [year, setYear] = useState(initialYear ?? moment().year());
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const {t} = useTranslation();

  const emitChange = (newSelected) => {
    if (mode === 'single' && newSelected.length === 1) {
      const { month, year } = newSelected[0];
      onChange?.({
        month_from: month + 1,
        month_to: null,
        year
      });
    } else if (mode === 'range' && newSelected.length > 1) {
      const sorted = newSelected.sort((a, b) =>
        moment({ year: a.year, month: a.month }).diff(moment({ year: b.year, month: b.month }))
      );
      const first = sorted[0];
      const last = sorted[sorted.length - 1];
      if (first.year === last.year) {
        onChange?.({
          month_from: first.month + 1,
          month_to: last.month + 1,
          year: first.year
        });
      } else {
        // Optional: handle multi-year range if needed
        onChange?.({
          month_from: first.month + 1,
          month_to: last.month + 1,
          year: `${first.year}-${last.year}`
        });
      }
    } else if (mode === 'multiple') {
      onChange?.(newSelected);
    }
  };

  const isDisabled = (monthIndex) => {
    if (!disabledFrom && !disabledTo) return false;
    
    const currentDate = moment({ year, month: monthIndex });
    const fromDate = disabledFrom ? moment({ year: disabledFrom.year, month: disabledFrom.month - 1 }) : null;
    const toDate = disabledTo ? moment({ year: disabledTo.year, month: disabledTo.month - 1 }) : null;

    if (fromDate && toDate) {
      return !currentDate.isBetween(fromDate, toDate, 'month', '[]');
    } else if (fromDate) {
      return currentDate.isBefore(fromDate, 'month');
    } else if (toDate) {
      return currentDate.isAfter(toDate, 'month');
    }
    return false;
  };

  const handleSelect = (monthIndex) => {
    if (isDisabled(monthIndex)) return;
    
    const newDate = { year, month: monthIndex };

    if (mode === 'single') {
      const newSelected = [newDate];
      setSelected(newSelected);
      setOpen(false);
      emitChange(newSelected);
    }

    if (mode === 'range') {
      if (selected.length === 0) {
        setSelected([newDate]);
      } else if (selected.length === 1) {
        const [first] = selected;
        const start = moment({ year: first.year, month: first.month });
        const end = moment({ year: newDate.year, month: newDate.month });

        const from = start.isBefore(end) ? start : end;
        const to = start.isBefore(end) ? end : start;

        const range = [];
        let current = from.clone();
        while (current.isSameOrBefore(to)) {
          range.push({ year: current.year(), month: current.month() });
          current.add(1, 'month');
        }

        setSelected(range);
        setOpen(false);
        emitChange(range);
      } else {
        setSelected([newDate]);
      }
    }

    if (mode === 'multiple') {
      const exists = selected.some(
        (m) => m.year === year && m.month === monthIndex
      );

      const newSelected = exists
        ? selected.filter((m) => !(m.year === year && m.month === monthIndex))
        : [...selected, newDate];

      if (newSelected.length <= maxSelectable) {
        setSelected(newSelected);
        emitChange(newSelected);
      }
    }
  };

  const isInRange = (monthIndex) => {
    if (selected.length < 2) return false;
    const monthsOnly = selected.map((m) => moment({ year: m.year, month: m.month }));
    const min = moment.min(monthsOnly);
    const max = moment.max(monthsOnly);
    const current = moment({ year, month: monthIndex });
    return current.isAfter(min) && current.isBefore(max);
  };

  const isRangeStart = (monthIndex) => {
    if (selected.length < 2) return false;
    const start = moment.min(selected.map(m => moment({ year: m.year, month: m.month })));
    return start.year() === year && start.month() === monthIndex;
  };

  const isRangeEnd = (monthIndex) => {
    if (selected.length < 2) return false;
    const end = moment.max(selected.map(m => moment({ year: m.year, month: m.month })));
    return end.year() === year && end.month() === monthIndex;
  };

  const isSelected = (monthIndex) =>
    selected.some((m) => m.year === year && m.month === monthIndex);

  const formatSelected = () => {
    if (selected.length === 0) return '';

    const sorted = selected.sort((a, b) =>
      moment({ year: a.year, month: a.month }).diff(moment({ year: b.year, month: b.month }))
    );

    if (mode === 'single' && sorted.length === 1) {
      const { month, year } = sorted[0];
      return `${months[month]}, ${year}`;
    }

    if (mode === 'range' && sorted.length > 1) {
      const first = sorted[0];
      const last = sorted[sorted.length - 1];

      const sameYear = first.year === last.year;
      return sameYear
        ? `${months[first.month]} – ${months[last.month]}, ${first.year}`
        : `${months[first.month]}, ${first.year} – ${months[last.month]}, ${last.year}`;
    }

    return sorted.map((m) => `${months[m.month]} ${m.year}`).join(', ');
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="month-picker" ref={containerRef}>
        <label htmlFor="month_picker" className='month-picker__label'>
            {label}
        </label>
      <input
        type="text"
        className={`month-picker__input${disabled ? ' month-picker__input--disabled' : ''}`}
        readOnly
        name='month_picker'
        id='month_picker'
        value={formatSelected()}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        placeholder={t('monthPicker.placeholder')}
        style={{ zIndex: 0 }}
      />

      {open && (
        <div className="month-picker__popup" style={{
          zIndex: 2,
        }}>
          <div className="month-picker__header">
            <button
              className="month-picker__nav-button"
              onClick={() => setYear((y) => y - 1)}
            >
              ‹
            </button>
            <span className="month-picker__year">{year}</span>
            <button
              className="month-picker__nav-button"
              onClick={() => setYear((y) => y + 1)}
            >
              ›
            </button>
          </div>

          <p className="month-picker__hint">
            {mode === 'multiple'
              ? t('monthPicker.multiple', {maxSelectable})
              : mode === 'range'
              ? t('monthPicker.range', {maxSelectable})
              : t('monthPicker.single')}
          </p>

          <div className="month-picker__grid">
          {months.map((label, i) => {
              const baseClass = 'month-picker__item';
              const selectedClass = isSelected(i) ? ' month-picker__item--selected' : '';
              const inRangeClass = isInRange(i) ? ' month-picker__item--in-range' : '';
              const startClass = isRangeStart(i) ? ' month-picker__item--range-start' : '';
              const endClass = isRangeEnd(i) ? ' month-picker__item--range-end' : '';
              const disabledClass = isDisabled(i) ? ' month-picker__item--disabled' : '';

              return (
                <div className="month-picker__item-wrapper" key={i}>
                  <div
                    className={`${baseClass}${selectedClass}${inRangeClass}${startClass}${endClass}${disabledClass}`}
                    onClick={() => handleSelect(i)}
                  >
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
